import React from "react";
import { Controller } from "react-hook-form";
import Select, { components } from "react-select";
import { CheckBoxCustomStyle } from "./checkboxStyle";

const NoSeparator = () => null;
const NoMultiValue = () => null;

const InputOption = React.memo(
  ({ children, isSelected, innerRef, innerProps, ...rest }) => (
    <components.Option {...rest} innerRef={innerRef} innerProps={innerProps}>
      <div
        className="select-box"
        style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: 8,
            borderRadius: "9999px",
          }}
        >
          <input
            type="checkbox"
            checked={isSelected}
            readOnly
            disabled={rest?.isDisabled}
            style={{
              accentColor: "#5B7FB4",
              height: 18,
              width: 18,
              borderRadius: "9999px",
              cursor: "pointer",
            }}
          />
        </div>
        <span>{children}</span>
      </div>
    </components.Option>
  )
);
InputOption.displayName = "InputOption";

const SimpleValueContainer = React.memo((props) => {
  const { hasValue, getValue, children } = props;
  const labels = hasValue ? getValue().map((o) => o.label).join(", ") : "";

  return (
    <components.ValueContainer {...props}>
      {children}
      {hasValue && (
        <div
          style={{
            position: "absolute",
            left: 12,
            right: 36,
            top: "50%",
            transform: "translateY(-50%)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: 1.2,
            pointerEvents: "none",
          }}
          title={labels}
        >
          {labels}
        </div>
      )}
    </components.ValueContainer>
  );
});
SimpleValueContainer.displayName = "SimpleValueContainer";

const ClickableControl = React.memo((props) => {
  const { selectProps } = props;
  return (
    <components.Control
      {...props}
      innerProps={{
        ...props.innerProps,
        onMouseDown: (e) => {
          if (e.button !== 0) return;
          e.preventDefault();
          if (selectProps.isDisabled) return;
          if (selectProps.menuIsOpen) {
            selectProps.onMenuClose?.();
          } else {
            selectProps.onMenuOpen?.();
          }
        },
      }}
    />
  );
});
ClickableControl.displayName = "ClickableControl";

const ClearIndicatorStopOpen = React.memo((props) => {
  const { clearValue, selectProps } = props;
  return (
    <components.ClearIndicator
      {...props}
      innerProps={{
        ...props.innerProps,
        onMouseDown: (e) => {
          e.preventDefault();
          e.stopPropagation();
          clearValue();
          selectProps.onMenuClose?.();
        },
        onTouchStart: (e) => {
          e.preventDefault();
          e.stopPropagation();
          clearValue();
          selectProps.onMenuClose?.();
        },
      }}
    />
  );
});
ClearIndicatorStopOpen.displayName = "ClearIndicatorStopOpen";


const MultiSelectField = ({
  name,
  control,
  label,
  options = [],
  className = "",
  placeholder,
  height,
  disabled = false,
  stylesOverride = {},
  onChange,
  value,
  defaultValue,
}) => {
  const containerRef = React.useRef(null);
  const [menuOpen, setMenuOpen] = React.useState(false);

  React.useEffect(() => {
    const onDocDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocDown, true);
    document.addEventListener("touchstart", onDocDown, { passive: true, capture: true });
    return () => {
      document.removeEventListener("mousedown", onDocDown, true);
      document.removeEventListener("touchstart", onDocDown, { capture: true });
    };
  }, []);

  const flatOptions = React.useMemo(() => {
    if (!Array.isArray(options)) return [];
    if (options.some((o) => Array.isArray(o?.options))) {
      return options.flatMap((g) => g?.options ?? []);
    }
    return options;
  }, [options]);

  const optionLookup = React.useMemo(
    () => new Map(flatOptions.map((o) => [String(o?.value), o])),
    [flatOptions]
  );

  const toOptionObjects = React.useCallback(
    (vals) => {
      if (!Array.isArray(vals)) return [];
      return vals
        .map((v) => {
          const key = typeof v === "object" && v !== null ? v.value : v;
          return optionLookup.get(String(key));
        })
        .filter(Boolean);
    },
    [optionLookup]
  );

  const styles = React.useMemo(() => {
    const base = {
      ...CheckBoxCustomStyle,
      control: (baseC) => ({
        ...baseC,
        borderColor: "#999",
        boxShadow: "none",
        borderRadius: "9999px",
        height: height ? height : "56px",
        cursor: "pointer",
        "&:hover": { borderColor: "#999" },
        "&:focus-within": { borderColor: "#2b364b" },
        alignItems: "center",
        overflow: "hidden",
        width: "100%",
      }),
      clearIndicator: (baseCI) => ({
        ...baseCI,
        cursor: "pointer",
        padding: 0,
      }),
      dropdownIndicator: (baseDI) => ({
        ...baseDI,
        cursor: "pointer",
        paddingRight: 6,
      }),
      valueContainer: (baseVC) => ({
        ...baseVC,
        position: "relative",
        flexWrap: "nowrap",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        paddingRight: 8,
        paddingLeft: 12,
      }),
      multiValue: () => ({ display: "none" }),
      multiValueLabel: (baseMVL) => ({ ...baseMVL, color: "#333", fontSize: "12px" }),
      multiValueRemove: (baseMVR) => ({
        ...baseMVR,
        color: "#999",
        cursor: "pointer",
        ":hover": { backgroundColor: "#e0e0e0" },
      }),
      input: (baseI) => ({ ...baseI, margin: 0, padding: 0, width: 1 }),
      container: (baseContainer) => ({ ...baseContainer, width: "100%" }),
    };

    return { ...base, ...stylesOverride };
  }, [height, stylesOverride]);

  const selectComponents = React.useMemo(
    () => ({
      Control: ClickableControl,
      Option: InputOption,
      IndicatorSeparator: NoSeparator,
      ValueContainer: SimpleValueContainer,
      MultiValue: NoMultiValue,
      ClearIndicator: ClearIndicatorStopOpen,
    }),
    []
  );

  /* Non-RHF-mode state — declared before the RHF branch so hooks run
     unconditionally in every render (rules-of-hooks). */
  const isControlled = Array.isArray(value);
  const [inner, setInner] = React.useState(Array.isArray(defaultValue) ? defaultValue : []);
  const currentValues = isControlled ? value : inner;
  const currentValueObjs = React.useMemo(() => toOptionObjects(currentValues), [currentValues, toOptionObjects]);

  const handleUncontrolledChange = React.useCallback(
    (selected, actionMeta) => {
      const nextOptions = selected || [];
      if (!isControlled) setInner(nextOptions.map((i) => i.value));
      if (typeof onChange === "function") onChange(nextOptions, actionMeta, currentValues);
    },
    [isControlled, onChange, currentValues]
  );

  /* ------------------------------ RHF MODE ------------------------------ */
  if (control && name) {
    return (
      <div ref={containerRef} className={`relative font-montserrat ${className}`}>
        {label && (
          <div className="absolute -top-2 left-4 z-10 text-sm bg-white px-2">
            <span className="text-sm font-montserrat text-gray-600">
              {label}
            </span>
          </div>
        )}

        <Controller
          control={control}
          name={name}
          render={({ field }) => {
            const valueObjs = toOptionObjects(field.value);
            const handleChange = (selected, actionMeta) => {
              const selectedValues = (selected || []).map((item) => item.value);
              field.onChange(selectedValues);
              if (typeof onChange === "function") onChange(selectedValues, actionMeta, field.value);
            };

            return (
              <Select
                {...field}
                className="focus:border-none focus:ring-0 focus:outline-none !border-none !ring-0"
                classNamePrefix="react-select"
                isMulti
                isClearable
                isSearchable={false}
                closeMenuOnSelect={false}
                placeholder={placeholder}
                options={options}
                value={valueObjs}
                onChange={handleChange}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                components={selectComponents}
                styles={styles}
                isDisabled={disabled}
                menuShouldBlockScroll={false}
                hideSelectedOptions={false}
                getOptionLabel={(o) => o.label}
                menuIsOpen={menuOpen}
                onMenuOpen={() => setMenuOpen(true)}
                onMenuClose={() => setMenuOpen(false)}
                openMenuOnClick
              />
            );
          }}
        />
      </div>
    );
  }

  /* ---------------------------- NON-RHF MODE ---------------------------- */
  return (
    <div ref={containerRef} className={`relative font-montserrat ${className}`}>
      {label && (
        <div className="absolute -top-2 left-4 z-10 text-sm bg-white px-2">
          <span className="text-sm font-montserrat text-gray-600">
            {label}
          </span>
        </div>
      )}

      <Select
        className="focus:border-none focus:ring-0 focus:outline-none !border-none !ring-0"
        classNamePrefix="react-select"
        isMulti
        isClearable
        isSearchable={false}
        closeMenuOnSelect={false}
        placeholder={placeholder}
        options={options}
        value={currentValueObjs}
        onChange={handleUncontrolledChange}
        components={selectComponents}
        styles={styles}
        isDisabled={disabled}
        menuShouldBlockScroll={false}
        hideSelectedOptions={false}
        getOptionLabel={(o) => o.label}
        menuIsOpen={menuOpen}
        onMenuOpen={() => setMenuOpen(true)}
        onMenuClose={() => setMenuOpen(false)}
        openMenuOnClick
      />
    </div>
  );
};

export default React.memo(MultiSelectField);
